import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Layout, Space, Button, Modal, message } from 'antd';
import { cloneDeep, unionBy } from 'lodash';
import FlagRed from '../../../../assets/flag_Red.svg';
import FlagOrange from '../../../../assets/flag_Orange.svg';
import { SCENARIO_RI_PASSED } from '../constants';
import styles from './style.scss';
import { TM_FRAUD_SOURCE_IDS } from '../../../../util/GlobalConstant';
import DerivedRiskIndicators from './DerivedIndicators/components/RiskIndicator';
import { Prompt } from 'react-router-dom';
import { useBeforeunload } from 'react-beforeunload';

const { Header, Footer, Content } = Layout;

const NavigationBlocker = ({ navigationBlocked, message }) => {
  useBeforeunload((event) => {
    if (navigationBlocked) {
      event.preventDefault();
      return message;
    }
  });

  return <Prompt when={navigationBlocked} message={message} />;
};

NavigationBlocker.propTypes = {
  navigationBlocked: PropTypes.bool,
  message: PropTypes.string
};

const RiskIndicators = ({
  scenarioId,
  riskAPIActions,
  riskIndicators,
  riskIndicator,
  onCancel,
  error,
  isSaved,
  scenarioSource,
  scenarioTypologies
}) => {
  const { fetchRIListByScenarioId, unMountRiskIndicators, putRIByScenarioId } =
    riskAPIActions;
  const [riskIndicatorList, setRiskIndicatorList] = useState([]);
  const [allRiskIndicators, setAllRiskIndicators] = useState([]);
  const [typologies, setTypologies] = useState([]);
  const [saveBtnStatus, setSaveBtnStatus] = useState(true);
  const [activePanel, setActivePanel] = useState(0);

  const { data } = scenarioTypologies;

  useEffect(() => {
    handleActivePanel(activePanel);
    setTypologies(data?.segmentCombinations ?? []);
  }, [data?.segmentCombinations]);

  useEffect(() => {
    if (isSaved) {
      message.success(SCENARIO_RI_PASSED, 1.5);
      setRiskIndicatorList([]);
      setAllRiskIndicators([]);
    }
  }, [isSaved]);

  useEffect(() => {
    if (error) message.error(error, 1.5);
  }, [error]);

  useEffect(() => {
    let list = cloneDeep(riskIndicatorList);
    list = unionBy(list, riskIndicators);
    setRiskIndicatorList(list);
    let allRI = cloneDeep(allRiskIndicators);
    allRI = unionBy(allRI, riskIndicators);
    setAllRiskIndicators(allRI);
  }, [riskIndicators]);

  useEffect(() => () => unMountRiskIndicators(), []);

  const onRiskIndicatorsCancel = () => {
    onCancel?.();
  };

  const onSaveConfirm = () => {
    const derived =
      riskIndicatorList.length > 0 ? { derived: riskIndicatorList } : {};
    const payload = {
      scenarioId,
      data: { ...derived }
    };
    setSaveBtnStatus(true);
    putRIByScenarioId(payload);
  };

  const onRiskIndicatorsSave = () => {
    Modal.confirm({
      title: 'Do you want to save changes?',
      content: `Risk Indicator Thresholds will be Saved.`,
      cancelText: 'Cancel',
      okText: 'Save',
      onOk: onSaveConfirm
    });
  };

  const userThresholdPattern = new RegExp(/^(-)?\d*\.?\d*$/);

  const handleUserThreshold = (
    typologyId,
    selectedCombinationId,
    index,
    value
  ) => {
    let riList = cloneDeep(riskIndicatorList);
    riList.map((ri) => {
      if (
        ri.id === typologyId &&
        ri?.segment?.id === selectedCombinationId &&
        ri?.derivedIndicators?.[index] &&
        (value === null || value === '' || userThresholdPattern.test(value))
      ) {
        ri.derivedIndicators[index].threshold.finalValue = value?.toString();
      }
      return ri;
    });
    setRiskIndicatorList(riList);
  };

  useMemo(() => {
    setSaveBtnStatus(
      JSON.stringify(allRiskIndicators) === JSON.stringify(riskIndicatorList)
    );
  }, [riskIndicatorList, allRiskIndicators]);

  const handleCopyRI = (typologyId, from, toList) => {
    let copyFrom = riskIndicatorList.find(
      (ri) => ri.id === typologyId && ri?.segment?.id === from
    );

    let riList = cloneDeep(riskIndicatorList);
    let combinationsRI = [];
    toList.map((toId) => {
      let index = riList.findIndex(
        (ri) => ri.id === typologyId && ri?.segment?.id === toId
      );
      if (index >= 0) {
        let finalThersholdValues = copyFrom?.derivedIndicators?.map(
          ({ threshold }) => threshold?.finalValue
        );
        riList[index].derivedIndicators.map(({ threshold }, index) => {
          threshold.finalValue = finalThersholdValues[index];
        });
      }
    });
    riList.push(...combinationsRI);
    setRiskIndicatorList(riList);
  };

  const handleActivePanel = (index) => {
    setActivePanel(index);
    let typologyId = data?.segmentCombinations?.[index]?.typology?.id;

    if (scenarioId && typologyId) {
      fetchRIListByScenarioId({ scenarioId, typologyId });
    }
  };

  return (
    <>
      <Content>
        <Space direction="vertical" className={styles.tabPanelSpace}>
          <Content className={styles.riskIndicatorBG}>
            <Layout>
              <Header>
                <p className={styles.instanceTitle}>
                  Risk Indicators
                  {scenarioSource === TM_FRAUD_SOURCE_IDS.TM && (
                    <span style={{ paddingLeft: '5px' }}>
                      |{' '}
                      <img
                        style={{ paddingLeft: '5px' }}
                        src={FlagOrange}
                        alt=""
                      />{' '}
                      AML-TM Detection
                    </span>
                  )}
                  {scenarioSource === TM_FRAUD_SOURCE_IDS.FRAUD && (
                    <span style={{ paddingLeft: '5px' }}>
                      |{' '}
                      <img
                        style={{ paddingLeft: '5px' }}
                        src={FlagRed}
                        alt=""
                      />{' '}
                      Fraud Detection
                    </span>
                  )}
                </p>
              </Header>
              <Content>
                <DerivedRiskIndicators
                  scenarioId={scenarioId}
                  typologies={typologies}
                  riskIndicators={riskIndicatorList}
                  riskIndicator={riskIndicator}
                  handleUserThreshold={handleUserThreshold}
                  handleCopyRI={handleCopyRI}
                  handleActivePanel={handleActivePanel}
                  isSaved={isSaved}
                />
              </Content>
            </Layout>
          </Content>
        </Space>
      </Content>
      <Footer
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '10px 20px'
        }}>
        <Button onClick={onRiskIndicatorsCancel} style={{ marginRight: 20 }}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={onRiskIndicatorsSave}
          disabled={saveBtnStatus}>
          Save
        </Button>
      </Footer>
      <NavigationBlocker
        navigationBlocked={
          JSON.stringify(allRiskIndicators) !==
          JSON.stringify(riskIndicatorList)
        }
        message="Leave page? Changes you made may not be saved."
      />
    </>
  );
};

RiskIndicators.propTypes = {
  scenarioId: PropTypes.number.isRequired,
  riskAPIActions: PropTypes.object.isRequired,
  riskIndicators: PropTypes.array,
  riskIndicator: PropTypes.any,
  onCancel: PropTypes.func,
  checkEditedValue: PropTypes.func,
  reset: PropTypes.bool,
  error: PropTypes.object,
  isSaved: PropTypes.bool,
  scenarioTypologies: PropTypes.object,
  scenarioSource: PropTypes.string
};

export default RiskIndicators;
